import { useQuery } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";

// Central registry of every editable text block across the public site.
// Each group maps to a page; each field has a stable key, a label shown in
// the admin editor, the default copy, and whether it's a multi-line textarea.
export const CONTENT_SCHEMA = [
  {
    page: "Landing / Home",
    fields: [
      { key: "landing.hero_cta_browse", label: "Hero button — Browse", default: "Browse the Collection" },
      { key: "landing.hero_cta_signin", label: "Hero button — Sign in", default: "Sign in to Reserve" },
      { key: "landing.categories_overline", label: "Categories — Overline", default: "Shop by Category" },
      { key: "landing.categories_title", label: "Categories — Title", default: "Twelve Curated Collections" },
      { key: "landing.featured_overline", label: "Featured — Overline", default: "Handpicked" },
      { key: "landing.featured_title", label: "Featured — Title", default: "Featured Rentals" },
      { key: "landing.faq_overline", label: "FAQ preview — Overline", default: "Good to Know" },
      { key: "landing.faq_title", label: "FAQ preview — Title", default: "Frequently Asked" },
      { key: "landing.faq_cta", label: "FAQ preview — Button", default: "See all FAQs" },
      { key: "landing.app_title", label: "App section — Title", default: "Get the Idea Events App" },
      { key: "landing.app_subtitle", label: "App section — Subtitle", textarea: true, default: "Browse the collection, manage reservations, and chat with your concierge — all from your pocket." },
    ],
  },
  {
    page: "Catalog",
    fields: [
      { key: "catalog.overline", label: "Overline", default: "The Collection" },
      { key: "catalog.title", label: "Title", default: "Catalog" },
      { key: "catalog.search_placeholder", label: "Search placeholder", default: "Search rentals…" },
      { key: "catalog.empty", label: "No results message", default: "No rentals match your search." },
    ],
  },
  {
    page: "Product Detail",
    fields: [
      { key: "product.back", label: "Back link", default: "Back to Catalog" },
      { key: "product.quantity", label: "Quantity label", default: "Quantity" },
      { key: "product.days", label: "Rental days label", default: "Rental Days" },
      { key: "product.subtotal", label: "Subtotal label", default: "Estimated subtotal" },
      { key: "product.stock_suffix", label: "Stock suffix", default: "available in inventory" },
      { key: "product.add_to_cart", label: "Add to cart button", default: "Add to Cart" },
      { key: "product.signin", label: "Sign in button", default: "Sign in to Reserve" },
    ],
  },
  {
    page: "Cart & Checkout",
    fields: [
      { key: "cart.title", label: "Title", default: "Cart & Checkout" },
      { key: "cart.details_title", label: "Event details title", default: "Event Details" },
      { key: "cart.empty_title", label: "Empty — Title", default: "Your cart is empty" },
      { key: "cart.empty_subtitle", label: "Empty — Subtitle", default: "Explore our luxury collection to start your reservation." },
      { key: "cart.empty_cta", label: "Empty — Button", default: "Browse Catalog" },
      { key: "cart.footer_note", label: "Checkout footer note", textarea: true, default: "Delivery is estimated by ZIP zone and finalized after review. Balance due before your event." },
    ],
  },
  {
    page: "Reservations",
    fields: [
      { key: "reservations.title", label: "Title", default: "My Reservations" },
      { key: "reservations.signin_title", label: "Signed-out — Title", default: "Sign in to view reservations" },
      { key: "reservations.signin_subtitle", label: "Signed-out — Subtitle", default: "Access your reservation history and status updates." },
      { key: "reservations.empty", label: "Empty message", default: "You have no reservations yet." },
      { key: "reservations.empty_cta", label: "Empty — Button", default: "Start Browsing" },
    ],
  },
  {
    page: "Contact",
    fields: [
      { key: "contact.overline", label: "Overline", default: "Get in Touch" },
      { key: "contact.title", label: "Title", default: "Contact Us" },
    ],
  },
  {
    page: "FAQs",
    fields: [
      { key: "faqs.overline", label: "Overline", default: "Good to Know" },
      { key: "faqs.title", label: "Title", default: "Frequently Asked Questions" },
    ],
  },
  {
    page: "Footer",
    fields: [
      { key: "footer.explore_heading", label: "Explore heading", default: "Explore" },
      { key: "footer.contact_heading", label: "Get in touch heading", default: "Get in touch" },
      { key: "footer.copyright", label: "Copyright text", default: "Idea Events USA. All rights reserved." },
    ],
  },
  {
    page: "Privacy Policy",
    fields: [
      { key: "privacy.title", label: "Title", default: "Privacy Policy" },
      { key: "privacy.intro", label: "Intro", textarea: true, default: 'Idea Events USA ("we", "us") operates ideaeventsusa.com. This policy explains what information we collect and how we use it. By using our site you agree to this policy.' },
      { key: "privacy.collect_h", label: "Section 1 heading", default: "Information we collect" },
      { key: "privacy.collect_b", label: "Section 1 body", textarea: true, default: "When you create a reservation or contact us, we collect your name, email, phone number, delivery address, and the details of your event and rental order. If you sign in with Google, we receive your basic Google profile (name, email, profile photo)." },
      { key: "privacy.use_h", label: "Section 2 heading", default: "How we use it" },
      { key: "privacy.use_b", label: "Section 2 body", textarea: true, default: "We use your information to process reservations, arrange delivery and setup, take payment, send order and balance-due notifications, provide customer support, and improve our services. We do not sell your personal information." },
      { key: "privacy.pay_h", label: "Section 3 heading", default: "Payments" },
      { key: "privacy.pay_b", label: "Section 3 body", textarea: true, default: "Payments are processed securely by Stripe. We do not store your full card number on our servers; card data is handled directly by our PCI-compliant payment processor." },
      { key: "privacy.cookies_h", label: "Section 4 heading", default: "Cookies & sessions" },
      { key: "privacy.cookies_b", label: "Section 4 body", textarea: true, default: "We use a secure session cookie to keep you signed in. We do not use third-party advertising trackers." },
      { key: "privacy.choices_h", label: "Section 5 heading", default: "Your choices" },
      { key: "privacy.choices_b", label: "Section 5 body", textarea: true, default: "You may request access to, correction of, or deletion of your account and personal data at any time by contacting us." },
    ],
  },
  {
    page: "Terms of Service",
    fields: [
      { key: "terms.title", label: "Title", default: "Terms of Service" },
      { key: "terms.intro", label: "Intro", textarea: true, default: "These terms govern your use of ideaeventsusa.com and any rental reservation you place with Idea Events USA." },
      { key: "terms.res_h", label: "Section 1 heading", default: "Reservations" },
      { key: "terms.res_b", label: "Section 1 body", textarea: true, default: "A reservation is a request to rent the listed items for your stated event dates. Availability and final pricing (including delivery) are confirmed by our team after review." },
      { key: "terms.dep_h", label: "Section 2 heading", default: "Deposit & balance" },
      { key: "terms.dep_b", label: "Section 2 body", textarea: true, default: "A 50% deposit is due at the time of reservation to secure your items. The remaining 50% balance is due before your event date. All payments are processed securely via Stripe." },
      { key: "terms.del_h", label: "Section 3 heading", default: "Delivery, care & return" },
      { key: "terms.del_b", label: "Section 3 body", textarea: true, default: "Delivery fees are estimated from your venue address and finalized on confirmation. You are responsible for the rented items from delivery until pickup and for any loss or damage beyond normal use." },
      { key: "terms.can_h", label: "Section 4 heading", default: "Cancellations" },
      { key: "terms.can_b", label: "Section 4 body", textarea: true, default: "Cancellation and refund terms depend on how far in advance you cancel; contact us as early as possible if your plans change." },
    ],
  },
];

export const CONTENT_DEFAULTS = CONTENT_SCHEMA.reduce((acc, g) => {
  g.fields.forEach((f) => { acc[f.key] = f.default; });
  return acc;
}, {});

// Hook returning a translate helper `t(key)` that prefers admin-edited copy
// and falls back to the registered default.
export function useContent() {
  const { data = {} } = useQuery({
    queryKey: ["content"],
    queryFn: async () => (await publicApi.get("/content")).data,
  });
  const t = (key) => {
    const v = data[key];
    return v !== undefined && v !== null && v !== "" ? v : CONTENT_DEFAULTS[key];
  };
  return { t, content: data };
}
